import { Toolbar } from '@plato/ui/primitives/toolbar';
import { Typography } from '@plato/ui/primitives/typography';
import { cn } from '@plato/ui/util/classes';
import { CircleAlertIcon } from 'lucide-react';
import type { ComponentPropsWithRef, ReactElement, ReactNode } from 'react';
import { MessageBubble } from './message-copy.tsx';

export type ChatErrorStateProps = ComponentPropsWithRef<'div'> & {
    /** The error line; omitted when nothing went wrong, e.g. an unclear reply that only offers an escalation. */
    title?: string;
    /** Assistant-styled bubble explaining the situation; omitted when the reply itself already says it. */
    explanation?: string;
    /** The actions that lead out of the state, as `ToolbarButton`s. */
    children: ReactNode;
};

/**
 * The state the chat shows when it cannot simply carry on: the reply failed,
 * the conversation closed, or the model asked something back and only an
 * escalation is offered. One layout for all of them, with an optional error
 * line, an optional explanation bubble, and the actions that lead out of the
 * state.
 */
export function ChatErrorState({
    className,
    title,
    explanation,
    children,
    ...props
}: ChatErrorStateProps): ReactElement {
    return (
        <div
            data-slot="chat-error-state"
            className={cn('flex flex-col items-start gap-default', className)}
            {...props}
        >
            {title !== undefined && (
                <div className="flex items-center gap-tight px-tight">
                    <CircleAlertIcon className="size-3 shrink-0 text-danger" />
                    <Typography variant="caption" className="text-danger">
                        {title}
                    </Typography>
                </div>
            )}
            {explanation !== undefined && (
                <MessageBubble variant="bubble" className="rounded-tl-tight" text={explanation} />
            )}
            <Toolbar className="flex-wrap">{children}</Toolbar>
        </div>
    );
}
